import React, { useState } from 'react';
import {
  Show,
  useRecordContext,
  useRedirect,
  useRefresh,
  useNotify,
  useTranslate,
} from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AccessTime,
  ArrowBack,
  Cancel as CancelIcon,
  CheckCircle,
  DeleteForever,
  Description,
  HourglassEmpty,
  Person,
  Receipt,
  Warning,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { API_CONFIG } from '../../config/api';
import { tokenManager } from '../../utils/tokenManager';

// Shared table style — matches ActiveSessionsShow exactly so visual rhythm is identical.
const detailsTableSx = {
  tableLayout: 'fixed' as const,
  width: '100%',
  '& .MuiTableCell-root': {
    px: { xs: 1, sm: 2 },
    py: { xs: 1, sm: 1.25 },
    verticalAlign: 'top',
    wordBreak: 'break-word' as const,
    overflowWrap: 'anywhere' as const,
    fontSize: { xs: '0.75rem', sm: '0.875rem' },
  },
  '& code': {
    wordBreak: 'break-all' as const,
    overflowWrap: 'anywhere' as const,
    fontSize: { xs: '0.7rem', sm: '0.8rem' },
  },
};

const getStatusColor = (status: string): 'success' | 'warning' | 'default' | 'error' => {
  switch (status) {
    case 'pending':
      return 'warning';
    case 'completed':
      return 'success';
    case 'cancelled':
      return 'default';
    default:
      return 'default';
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'pending':
      return <HourglassEmpty />;
    case 'completed':
      return <CheckCircle />;
    case 'cancelled':
      return <CancelIcon />;
    default:
      return <DeleteForever />;
  }
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) return '-';
  try {
    return format(new Date(value), 'PPpp');
  } catch {
    return String(value);
  }
};

// Page header — mirrors ActiveSessionsShow/PageHeader (big IconButton, bold title, status + subtitle row)
const PageHeader = () => {
  const record = useRecordContext();
  const redirect = useRedirect();
  const translate = useTranslate();

  if (!record) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: { xs: 2, sm: 4 },
        flexWrap: 'wrap',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton
          onClick={() => redirect('/account-deletion-requests')}
          size="large"
          sx={{
            flexShrink: 0,
            backgroundColor: '#FFFFFF',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            '&:hover': {
              backgroundColor: '#F5F5F5',
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            },
          }}
        >
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: '1.25rem', sm: '2.125rem' },
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
            }}
          >
            {translate('resources.account-deletion-requests.show.title', { _: 'Account Deletion Request' })}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={getStatusIcon(record.status)}
              label={translate(`resources.account-deletion-requests.statuses.${record.status}`, {
                _: record.status?.charAt(0).toUpperCase() + record.status?.slice(1),
              })}
              color={getStatusColor(record.status) as any}
              size="small"
            />
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                fontSize: { xs: '0.7rem', sm: '0.875rem' },
                wordBreak: 'break-all',
                overflowWrap: 'anywhere',
              }}
            >
              #{record.id}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// Summary card — status-colored hero at the top. Mirrors the "Charging Progress" card's structure from ActiveSessionsShow.
const SummaryCard = () => {
  const record = useRecordContext();
  const translate = useTranslate();

  if (!record) return null;

  const days = record.days_until_auto_process;
  const overdue = typeof days === 'number' && days < 0;
  const hasActive = record.has_active_charging_session === true;

  const bgColor =
    record.status === 'completed'
      ? '#E8F5E9'
      : record.status === 'cancelled'
      ? '#F5F5F5'
      : overdue
      ? '#FFEBEE'
      : '#FFF8E1';

  const iconBg =
    record.status === 'completed'
      ? 'success.light'
      : record.status === 'cancelled'
      ? 'grey.300'
      : overdue
      ? 'error.light'
      : 'warning.light';

  const iconColor =
    record.status === 'completed'
      ? 'success.main'
      : record.status === 'cancelled'
      ? 'grey.700'
      : overdue
      ? 'error.main'
      : 'warning.main';

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden', backgroundColor: bgColor }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" mb={{ xs: 2, sm: 3 }}>
          <Box
            sx={{
              width: { xs: 40, sm: 48 },
              height: { xs: 40, sm: 48 },
              borderRadius: 2,
              backgroundColor: iconBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
              color: iconColor,
              '& > svg': { fontSize: 28 },
            }}
          >
            {getStatusIcon(record.status)}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              fontWeight="600"
              sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              {record.status === 'pending'
                ? translate('resources.account-deletion-requests.show.summaryPending', {
                    _: 'Awaiting processing',
                  })
                : record.status === 'completed'
                ? translate('resources.account-deletion-requests.show.summaryCompleted', {
                    _: 'Account anonymized',
                  })
                : translate('resources.account-deletion-requests.show.summaryCancelled', {
                    _: 'Request cancelled',
                  })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDate(record.requested_at)}
            </Typography>
          </Box>
        </Box>

        {record.status === 'pending' && (
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 2, sm: 3 },
              flexWrap: 'wrap',
            }}
          >
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <AccessTime sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {translate('resources.account-deletion-requests.fields.autoProcess', {
                    _: 'Auto-process in',
                  })}
                </Typography>
              </Box>
              <Typography
                variant="h6"
                fontWeight="bold"
                color={overdue ? 'error.main' : 'inherit'}
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                {typeof days === 'number'
                  ? overdue
                    ? translate('resources.account-deletion-requests.fields.overdueFull', {
                        days: -days,
                        _: `${-days} days overdue`,
                      })
                    : translate('resources.account-deletion-requests.fields.daysUntilFull', {
                        days,
                        _: `${days} days`,
                      })
                  : '-'}
              </Typography>
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Warning sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {translate('resources.account-deletion-requests.fields.activeChargingSession', {
                    _: 'Active charging session',
                  })}
                </Typography>
              </Box>
              <Typography
                variant="h6"
                fontWeight="bold"
                color={hasActive ? 'warning.main' : 'inherit'}
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                {hasActive
                  ? translate('resources.account-deletion-requests.fields.yes', { _: 'Yes' })
                  : translate('resources.account-deletion-requests.fields.no', { _: 'No' })}
              </Typography>
            </Box>
          </Box>
        )}

        {record.status === 'pending' && hasActive && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {translate('resources.account-deletion-requests.warnings.activeSession', {
              _: 'User has an active charging session. Cancel the session first, then process.',
            })}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

// Section card helper — mirrors SessionInfo / ChargerInfo pattern (icon tile + h6 title + table)
const SectionCard: React.FC<{
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  title: string;
  children: React.ReactNode;
}> = ({ icon, iconBg, iconColor, title, children }) => (
  <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
      <Box display="flex" alignItems="center" mb={{ xs: 2, sm: 3 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            backgroundColor: iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2,
            color: iconColor,
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="h6"
          fontWeight="600"
          sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
        >
          {title}
        </Typography>
      </Box>
      <TableContainer sx={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <Table size="small" sx={detailsTableSx}>
          <TableBody>{children}</TableBody>
        </Table>
      </TableContainer>
    </CardContent>
  </Card>
);

const RequestInfo = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record) return null;

  return (
    <SectionCard
      icon={<Receipt />}
      iconBg="primary.light"
      iconColor="primary.main"
      title={translate('resources.account-deletion-requests.sections.request', { _: 'Request Details' })}
    >
      <TableRow>
        <TableCell sx={{ width: { xs: '40%', sm: '35%' } }}>
          <strong>
            {translate('resources.account-deletion-requests.fields.id', { _: 'Request ID' })}
          </strong>
        </TableCell>
        <TableCell>
          <code>{record.id}</code>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell>
          <strong>
            {translate('resources.account-deletion-requests.fields.requestedAt', {
              _: 'Requested at',
            })}
          </strong>
        </TableCell>
        <TableCell>{formatDate(record.requested_at)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell>
          <strong>
            {translate('resources.account-deletion-requests.fields.phone', { _: 'Phone (submitted)' })}
          </strong>
        </TableCell>
        <TableCell>{record.phone || '-'}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell>
          <strong>
            {translate('resources.account-deletion-requests.fields.email', { _: 'Email (submitted)' })}
          </strong>
        </TableCell>
        <TableCell>{record.email || '-'}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell>
          <strong>
            {translate('resources.account-deletion-requests.fields.reason', { _: 'Reason' })}
          </strong>
        </TableCell>
        <TableCell>
          {record.reason || (
            <Typography component="span" variant="body2" color="text.secondary" fontStyle="italic">
              {translate('resources.account-deletion-requests.fields.noReason', {
                _: 'No reason provided',
              })}
            </Typography>
          )}
        </TableCell>
      </TableRow>
    </SectionCard>
  );
};

const UserInfo = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record) return null;

  const summary = record.app_user_summary;
  const hasActive = record.has_active_charging_session === true;

  if (!summary) {
    return (
      <SectionCard
        icon={<Person />}
        iconBg="info.light"
        iconColor="info.main"
        title={translate('resources.account-deletion-requests.sections.user', { _: 'User' })}
      >
        <TableRow>
          <TableCell colSpan={2}>
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              {translate('resources.account-deletion-requests.fields.userNotFound', {
                _: 'User row not found',
              })}
            </Typography>
          </TableCell>
        </TableRow>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      icon={<Person />}
      iconBg="info.light"
      iconColor="info.main"
      title={translate('resources.account-deletion-requests.sections.user', { _: 'User' })}
    >
      <TableRow>
        <TableCell sx={{ width: { xs: '40%', sm: '35%' } }}>
          <strong>
            {translate('resources.account-deletion-requests.fields.userId', { _: 'User ID' })}
          </strong>
        </TableCell>
        <TableCell>
          <code>{summary.id || '-'}</code>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell>
          <strong>
            {translate('resources.account-deletion-requests.fields.name', { _: 'Name' })}
          </strong>
        </TableCell>
        <TableCell>
          {`${summary.first_name || ''} ${summary.last_name || ''}`.trim() || '-'}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell>
          <strong>
            {translate('resources.account-deletion-requests.fields.phone', { _: 'Phone' })}
          </strong>
        </TableCell>
        <TableCell>{summary.phone || '-'}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell>
          <strong>
            {translate('resources.account-deletion-requests.fields.email', { _: 'Email' })}
          </strong>
        </TableCell>
        <TableCell>{summary.email || '-'}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell>
          <strong>
            {translate('resources.account-deletion-requests.fields.isActive', { _: 'Is active' })}
          </strong>
        </TableCell>
        <TableCell>
          <Chip
            label={
              summary.is_active
                ? translate('resources.account-deletion-requests.fields.yes', { _: 'Yes' })
                : translate('resources.account-deletion-requests.fields.no', { _: 'No' })
            }
            color={summary.is_active ? 'success' : 'default'}
            size="small"
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell>
          <strong>
            {translate('resources.account-deletion-requests.fields.pendingDeletionAt', {
              _: 'Pending deletion at',
            })}
          </strong>
        </TableCell>
        <TableCell>{formatDate(summary.pending_deletion_at)}</TableCell>
      </TableRow>
      {summary.deleted_at && (
        <TableRow>
          <TableCell>
            <strong>
              {translate('resources.account-deletion-requests.fields.deletedAt', { _: 'Deleted at' })}
            </strong>
          </TableCell>
          <TableCell>{formatDate(summary.deleted_at)}</TableCell>
        </TableRow>
      )}
      <TableRow>
        <TableCell>
          <strong>
            {translate('resources.account-deletion-requests.fields.activeChargingSession', {
              _: 'Active charging session',
            })}
          </strong>
        </TableCell>
        <TableCell>
          <Chip
            label={
              hasActive
                ? translate('resources.account-deletion-requests.fields.yes', { _: 'Yes' })
                : translate('resources.account-deletion-requests.fields.no', { _: 'No' })
            }
            color={hasActive ? 'warning' : 'default'}
            size="small"
          />
        </TableCell>
      </TableRow>
    </SectionCard>
  );
};

const ProcessingInfo = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record) return null;
  if (!record.processed_at && !record.processed_by && !record.notes) return null;

  return (
    <SectionCard
      icon={<Description />}
      iconBg="success.light"
      iconColor="success.main"
      title={translate('resources.account-deletion-requests.sections.processing', { _: 'Processing' })}
    >
      <TableRow>
        <TableCell sx={{ width: { xs: '40%', sm: '35%' } }}>
          <strong>
            {translate('resources.account-deletion-requests.fields.processedAt', {
              _: 'Processed at',
            })}
          </strong>
        </TableCell>
        <TableCell>{formatDate(record.processed_at)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell>
          <strong>
            {translate('resources.account-deletion-requests.fields.processedBy', {
              _: 'Processed by',
            })}
          </strong>
        </TableCell>
        <TableCell>
          {record.processed_by ? (
            <code>{record.processed_by}</code>
          ) : record.status === 'completed' ? (
            translate('resources.account-deletion-requests.fields.processedAutomatically', {
              _: 'Automatic (30 days)',
            })
          ) : (
            '-'
          )}
        </TableCell>
      </TableRow>
      {record.notes && (
        <TableRow>
          <TableCell>
            <strong>
              {translate('resources.account-deletion-requests.fields.notes', { _: 'Notes' })}
            </strong>
          </TableCell>
          <TableCell>{record.notes}</TableCell>
        </TableRow>
      )}
    </SectionCard>
  );
};

const ProcessAction = () => {
  const record = useRecordContext();
  const refresh = useRefresh();
  const notify = useNotify();
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!record || record.status === 'completed' || record.status === 'cancelled') return null;

  const hasActive = record.has_active_charging_session === true;

  const handleProcess = async () => {
    setBusy(true);
    try {
      const token = await tokenManager.getAccessToken();
      const res = await fetch(
        `${API_CONFIG.baseURL}/api/backend/account-deletion-requests/${record.id}/process`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const body: any = await res.json().catch(() => ({}));

      if (res.ok && body.ok) {
        notify(
          body.status === 'already_completed'
            ? translate('resources.account-deletion-requests.notifications.alreadyProcessed', {
                _: 'Already processed.',
              })
            : translate('resources.account-deletion-requests.notifications.processed', {
                _: 'Account deletion processed.',
              }),
          { type: 'success' }
        );
        refresh();
        setOpen(false);
      } else if (res.status === 409 && body.error_code === 'ACTIVE_SESSION') {
        notify(
          body.message ||
            translate('resources.account-deletion-requests.errors.activeSession', {
              _: 'User has an active charging session. Cancel it first, then retry.',
            }),
          { type: 'warning' }
        );
      } else {
        notify(
          body.message ||
            body.error_code ||
            translate('resources.account-deletion-requests.errors.processFailed', {
              status: res.status,
              _: `Failed to process (HTTP ${res.status})`,
            }),
          { type: 'error' }
        );
      }
    } catch (err: any) {
      notify(`Failed: ${err?.message || err}`, { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" mb={{ xs: 2, sm: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              backgroundColor: 'error.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
            }}
          >
            <DeleteForever sx={{ color: 'error.main' }} />
          </Box>
          <Typography
            variant="h6"
            fontWeight="600"
            sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
          >
            {translate('resources.account-deletion-requests.sections.action', {
              _: 'Process Deletion',
            })}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {translate('resources.account-deletion-requests.show.actionDescription', {
            _: 'Processing will anonymize the user, delete wallet/cards/vehicles, deactivate RFID cards, revoke Supabase auth, and email the user if an email is on file.',
          })}
        </Typography>
        <Button
          color="error"
          variant="contained"
          size={isMobile ? 'medium' : 'large'}
          startIcon={<DeleteForever />}
          onClick={() => setOpen(true)}
          disabled={hasActive}
          fullWidth={isMobile}
        >
          {translate('resources.account-deletion-requests.actions.process', {
            _: 'Process deletion',
          })}
        </Button>
      </CardContent>

      <Dialog open={open} onClose={() => !busy && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {translate('resources.account-deletion-requests.dialog.title', {
            _: 'Permanently delete this account?',
          })}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body2">
              <strong>
                {translate('resources.account-deletion-requests.fields.phone', { _: 'Phone' })}:
              </strong>{' '}
              {record.app_user_summary?.phone ||
                translate('resources.account-deletion-requests.fields.alreadyAnonymized', {
                  _: '[already anonymized]',
                })}
            </Typography>
            <Typography variant="body2">
              <strong>
                {translate('resources.account-deletion-requests.fields.email', { _: 'Email' })}:
              </strong>{' '}
              {record.app_user_summary?.email ||
                translate('resources.account-deletion-requests.fields.alreadyAnonymized', {
                  _: '[already anonymized]',
                })}
            </Typography>
            <Alert severity="error" sx={{ mt: 1 }}>
              {translate('resources.account-deletion-requests.dialog.warning', {
                _: 'This will anonymize the user, delete wallet/cards/vehicles, deactivate RFID cards, revoke Supabase auth, and email the user if an email is on file. This cannot be undone.',
              })}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={busy}>
            {translate('ra.action.cancel', { _: 'Cancel' })}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleProcess}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={16} /> : <DeleteForever />}
          >
            {translate('resources.account-deletion-requests.actions.confirmDelete', {
              _: 'Confirm delete',
            })}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

const RequestDetails = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <PageHeader />
      <SummaryCard />
      <RequestInfo />
      <UserInfo />
      <ProcessingInfo />
      <ProcessAction />
    </Box>
  );
};

export const AccountDeletionRequestsShow: React.FC = () => (
  <Show
    actions={false}
    component="div"
    sx={{
      '& .RaShow-card': {
        backgroundColor: 'transparent',
        boxShadow: 'none',
      },
    }}
  >
    <RequestDetails />
  </Show>
);

export default AccountDeletionRequestsShow;
